import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { organizationStore } from '@core/org/organizationStore';

/**
 * Clears every cache when the signed-in user changes.
 *
 * Without this, signing out and back in as a different user renders the previous
 * tenant's trucks from cache for a few hundred milliseconds. On a multi-tenant fleet
 * product that is a cross-tenant data exposure, not a cosmetic glitch -- and it is
 * exactly the kind of thing that only shows up on a shared machine.
 *
 * The ref starts `undefined` rather than null so the very first render (userId going
 * from undefined to a value, or to null when signed out) does not count as a change.
 */
export function SessionSync() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const previous = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (previous.current !== undefined && previous.current !== userId) {
      // The organization selection belongs to the previous person as much as the cache
      // does: carried over, the next user's first request would name a tenant they may
      // not belong to.
      organizationStore.clear();
      queryClient.clear();
    }
    previous.current = userId;
  }, [userId, queryClient]);

  return null;
}
